import {
  QueryClient,
  useMutation,
  UseMutationResult,
} from '@tanstack/react-query';
import {
  handleMissingActivityNoticed,
  handleMissingHoursNoticed,
  hideJobForMember,
} from '@company/services/job/api';
import IJobDetail, { IJobsData } from '@th-types/job.detail.type';
import {
  IJobListFilters,
  IJobShiftsResult,
  IJobShiftsCounters,
} from '@th-types/jobs.type';

type UseHideJobMutationProps = {
  jobListFilters: IJobListFilters;
  setJobListFilters: React.Dispatch<React.SetStateAction<IJobListFilters>>;
  jobList: IJobsData | undefined;
  queryClient: QueryClient;
  jobListQuery: string[];
};

export function useHideJobMutation({
  jobList,
  queryClient,
  jobListQuery,
  jobListFilters,
  setJobListFilters,
}: UseHideJobMutationProps): UseMutationResult<
  any,
  unknown,
  IJobDetail,
  unknown
> {
  return useMutation({
    mutationFn: (job: IJobDetail) => hideJobForMember(job.id),
    onMutate: async (job: IJobDetail) => {
      queryClient.setQueryData(jobListQuery, (currentJobList: IJobsData) => {
        return {
          ...currentJobList,
          results: currentJobList.results.filter((item) => item.id !== job.id),
        };
      });
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: jobListQuery });

      console.error('An error occurred hiding job:', error);
    },
    onSuccess: () => {
      const countHiddenJobs = jobList?.countHiddenJobs || 0;
      const isEmptyList = countHiddenJobs === 1 && jobListFilters.hiddenJobs;

      queryClient.setQueryData(jobListQuery, (currentJobList: IJobsData) => {
        return {
          ...currentJobList,
          countHiddenJobs: jobListFilters.hiddenJobs
            ? currentJobList.countHiddenJobs - 1
            : currentJobList.countHiddenJobs + 1,
        };
      });

      if (isEmptyList) {
        setJobListFilters((prev) => ({
          ...prev,
          hiddenJobs: false,
        }));
      }
    },
    onSettled: () => {},
  });
}

export type UseMissingActivitiesNoticedMutationProps = {
  queryJobShiftsList: string[];
  queryJobShiftsCounters: string[];
  queryClient: QueryClient;
};

export function useMissingActivitiesNoticedMutation({
  queryClient,
  queryJobShiftsList,
  queryJobShiftsCounters,
}: UseMissingActivitiesNoticedMutationProps) {
  return useMutation({
    mutationFn: (shiftPositionId: number) =>
      handleMissingActivityNoticed(shiftPositionId),
    onMutate: async (shiftPositionId: number) => {
      let isNoticed: boolean | null;
      queryClient.setQueryData(
        queryJobShiftsList,
        (currentJobShiftsListResult: IJobShiftsResult) => {
          return {
            ...currentJobShiftsListResult,
            jobShifts: currentJobShiftsListResult.jobShifts.map(
              (jobShiftsItem) => {
                // Check if this jobShiftsItem contains the target activity
                const hasTargetActivity = jobShiftsItem.jobShiftActivities.some(
                  (activity) => activity.shiftPosition.id === shiftPositionId
                );

                if (!hasTargetActivity) {
                  return jobShiftsItem;
                }

                return {
                  ...jobShiftsItem,
                  jobShiftActivities: jobShiftsItem.jobShiftActivities.map(
                    (activityItem) => {
                      if (activityItem.shiftPosition.id === shiftPositionId) {
                        isNoticed =
                          activityItem.shiftPosition.noticedMissingActivities;
                        return {
                          ...activityItem,
                          shiftPosition: {
                            ...activityItem.shiftPosition,
                            noticedMissingActivities:
                              !activityItem.shiftPosition
                                .noticedMissingActivities,
                          },
                        };
                      }
                      return activityItem;
                    }
                  ),
                };
              }
            ),
          };
        }
      );
      queryClient.setQueryData(
        queryJobShiftsCounters,
        (currentCounters: IJobShiftsCounters) => {
          return {
            ...currentCounters,
            countNoCheckInOut: isNoticed
              ? currentCounters.countNoCheckInOut + 1
              : currentCounters.countNoCheckInOut - 1,
          };
        }
      );
    },
    onError: (error) => {
      // Rollback optimistic update on error
      queryClient.invalidateQueries({
        queryKey: queryJobShiftsList,
      });
      queryClient.invalidateQueries({
        queryKey: queryJobShiftsCounters,
      });
      console.error(
        'Failed to update missing activities noticed status:',
        error
      );
    },
  });
}

export type UseMissingHoursNoticedMutationProps = {
  queryJobShiftsList: string[];
  queryJobShiftsCounters: string[];
  queryClient: QueryClient;
};

export function useMissingHoursNoticedMutation({
  queryClient,
  queryJobShiftsList,
  queryJobShiftsCounters,
}: UseMissingHoursNoticedMutationProps) {
  return useMutation({
    mutationFn: (shiftPositionId: number) =>
      handleMissingHoursNoticed(shiftPositionId),
    onMutate: async (shiftPositionId: number) => {
      let isNoticed: boolean | null;
      queryClient.setQueryData(
        queryJobShiftsList,
        (currentJobShiftsListResult: IJobShiftsResult) => {
          return {
            ...currentJobShiftsListResult,
            jobShifts: currentJobShiftsListResult.jobShifts.map(
              (jobShiftsItem) => {
                // Check if this jobShiftsItem contains the target activity
                const hasTargetActivity = jobShiftsItem.jobShiftActivities.some(
                  (activity) => activity.shiftPosition.id === shiftPositionId
                );

                if (!hasTargetActivity) {
                  return jobShiftsItem;
                }

                return {
                  ...jobShiftsItem,
                  jobShiftActivities: jobShiftsItem.jobShiftActivities.map(
                    (activityItem) => {
                      if (activityItem.shiftPosition.id === shiftPositionId) {
                        isNoticed =
                          activityItem.shiftPosition.noticedMissingHours;
                        return {
                          ...activityItem,
                          shiftPosition: {
                            ...activityItem.shiftPosition,
                            noticedMissingHours:
                              !activityItem.shiftPosition.noticedMissingHours,
                          },
                        };
                      }
                      return activityItem;
                    }
                  ),
                };
              }
            ),
          };
        }
      );
      queryClient.setQueryData(
        queryJobShiftsCounters,
        (currentCounters: IJobShiftsCounters) => {
          return {
            ...currentCounters,
            countMissingHours: isNoticed
              ? currentCounters.countMissingHours + 1
              : currentCounters.countMissingHours - 1,
          };
        }
      );
    },
    onError: (error) => {
      // Rollback optimistic update on error
      queryClient.invalidateQueries({
        queryKey: queryJobShiftsList,
      });
      queryClient.invalidateQueries({
        queryKey: queryJobShiftsCounters,
      });
      console.error('Failed to update missing hours noticed status:', error);
    },
  });
}
